import React, { InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import s from './DurationSlider.scss';

export type DurationValue = 2 | 4 | 6 | 8;

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'min' | 'max' | 'step' | 'value' | 'onChange'> {
  /** Current selected duration in days */
  value: DurationValue;
  /** Callback when duration changes */
  onChange: (duration: DurationValue) => void;
  /** Additional CSS class name */
  className?: string;
  /** Whether the slider is disabled */
  disabled?: boolean;
}

const DURATIONS: DurationValue[] = [2, 4, 6, 8];
const DURATION_TO_INDEX: Record<DurationValue, number> = {
  2: 0,
  4: 1,
  6: 2,
  8: 3
};
const INDEX_TO_DURATION: Record<number, DurationValue> = {
  0: 2,
  1: 4,
  2: 6,
  3: 8
};

const DurationSlider: React.FC<Props> = ({
  value,
  onChange,
  className,
  disabled = false,
  ...rest
}) => {
  const { t } = useTranslation();
  const currentIndex = DURATION_TO_INDEX[value];

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(event.target.value, 10);
    const duration = INDEX_TO_DURATION[index];
    if (duration) {
      onChange(duration);
    }
  };

  return (
    <div className={c(s['duration-slider'], className)}>
      <div className={s['slider-container']}>
        <input
          {...rest}
          type="range"
          min={0}
          max={3}
          step={1}
          value={currentIndex}
          onChange={handleChange}
          disabled={disabled}
          className={s['slider']}
          aria-label={t('boostDeviation.modal.chooseDurationAriaLabel')}
        />
        <div className={s['labels']}>
          {DURATIONS.map((duration, index) => (
            <button
              key={duration}
              type="button"
              className={c(s['label'], {
                [s['label--active']]: index === currentIndex,
                [s['label--disabled']]: disabled
              })}
              onClick={() => !disabled && onChange(duration)}
              disabled={disabled}
              aria-pressed={index === currentIndex}
              aria-label={t('boostDeviation.modal.durationDaysAriaLabel', { duration })}
            >
              {t('boostDeviation.modal.durationDays', { duration })}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

DurationSlider.displayName = 'DurationSlider';

export default DurationSlider;
